import { useQueryClient } from '@tanstack/react-query';
import { OnboardingAuditFieldIdentifier, useRepositoryMutation } from '@mews-commander/data-access';
import { CommanderFormProps, useDialogForm, useHandledDialogFormControls } from '@mews-commander/form';
import { createZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { AuditName } from '../../components/AuditName';
import { FormValues, schema } from './schema';
import { UpdateOnboardingAuditFormContent } from './UpdateOnboardingAuditFormContent';
import { useInitialValues } from './useInitialValues';

interface WithFieldIdentifier {
    fieldIdentifier: OnboardingAuditFieldIdentifier;
}

export const useUpdateOnboardingAudit = (fieldIdentifier: OnboardingAuditFieldIdentifier) => {
    return useDialogForm({
        id: `overrideForm-${fieldIdentifier}`,
        cancelLabel: lt('Close'),
        title: (
            <>
                {lt('OnboardingAuditOverrideFormTitle')}
                <AuditName fieldIdentifier={fieldIdentifier} />
            </>
        ),
        renderForm: (props) => <UpdateOnboardingAuditForm {...props} fieldIdentifier={fieldIdentifier} />,
    });
};

const UpdateOnboardingAuditForm = ({ fieldIdentifier, ...other }: CommanderFormProps & WithFieldIdentifier) => {
    const queryClient = useQueryClient();
    const initialValues = useInitialValues(fieldIdentifier);

    const { mutateAsync } = useRepositoryMutation('enterprises', 'setOnboardingAuditOverride', {
        invalidateQueries: {
            enterprises: ['getOnboardingAudit'],
        },
        onSuccess: ({ onboardingAuditOverride }) => {
            showSuccessAlert({
                title: lt('ChangesSaved'),
            });
            queryClient.setQueryData(
                [
                    'enterprises',
                    'getOnboardingAuditOverride',
                    { fieldIdentifiers: [onboardingAuditOverride?.fieldIdentifier] },
                ],
                { onboardingAuditOverrides: [onboardingAuditOverride] }
            );
        },
    });

    const { renderFormContent, resetField } = useHandledDialogFormControls<FormValues>({
        values: initialValues,
        onSubmit: async (values) => mutateAsync({ ...values, fieldIdentifier }),
        resolver: createZodFormResolver(schema),
        ...other,
    });

    const resetStatusField = () => resetField('overrideStatus', { defaultValue: null });

    return renderFormContent(
        <UpdateOnboardingAuditFormContent
            manualOverride={initialValues.overrideStatus !== null}
            resetStatusField={resetStatusField}
        />
    );
};
